import { Box, Flex } from '@sequencehq/core-components'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { useNavigate } from 'react-router-dom'
import { match } from 'ts-pattern'
import { automationsPaths } from 'Automations/router'
import { InboxList } from '../InboxList/InboxList'
import { useAllAutomationExecutions } from 'Automations/automationExecutions/hooks/useAllAutomationExecutions'
import { useSelectedAutomationExecution } from '../../hooks/useSelectedAutomationExecution'
import { AutomationExecutionItem } from 'Automations/automationExecutions/inbox/AutomationExecutionItem'
import { ListWithPagination } from 'Automations/components'
import { ExecutionFilterPills } from 'Automations/automationExecutions/inbox/AutomationExecutionFilters'
import { ExecutionAddFilter } from 'Automations/automationExecutions/inbox/ExecutionAddFilter'
import { AdminSettingsButton } from 'Automations/components'
import { useAtomValue } from 'jotai'
import { adminViewEnabledAtom } from 'Automations/watchtower/inbox/manualTasks/manualTasksInbox/atoms'
import {
  mapExecutionStatusToManualTaskStatus,
  watchtowerFiltersAtom
} from 'Automations/watchtower/atoms'
import {
  AutomationExecution,
  ExecutionFilters,
  LegacyWorkflow
} from 'Automations/automationExecutions/types'
import { useCurrentUser } from 'common/hooks'
import { useManualTasks } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTasks'
import { ManualTaskItem } from 'Automations/watchtower/inbox/manualTasks/manualTasksInbox/components/manualTasksList'
import { ManualTask } from 'Automations/watchtower/inbox/manualTasks/types'
import { useLegacyWorkflows } from 'Automations/automationExecutions/hooks/useLegacyWorkflows'
import { LegacyWorkflowItem } from 'Automations/automationExecutions/legacyWorkflows/LegacyWorkflowItem'
import {
  prefetchExecutionSummary,
  prefetchLegacyWorkflowSummary
} from 'Automations/watchtower/inbox/prefetchExecutionSummary'
import { useShouldUseAdminManualTasks } from 'Automations/watchtower/inbox/manualTasks/hooks/useShouldUseAdminManualTasks'

type ManualTaskListItem = {
  kind: 'manualTask'
  executionId: string
  createdAt: string
  data: ManualTask
}

type ExecutionListItem = {
  kind: 'execution'
  executionId: string
  createdAt: string
  data: AutomationExecution
}

type LegacyWorkflowListItem = {
  kind: 'legacyWorkflow'
  executionId: string
  createdAt: string
  data: LegacyWorkflow
}

type ListItem = ManualTaskListItem | ExecutionListItem | LegacyWorkflowListItem

// Dedup key for a manual task in the history view, keyed on its execution id.
// Execution-less tasks (customer-info reviews, notifications) return null: they
// belong in the Watchtower "Needs action" inbox, not the per-workflow history.
const getExecutionsViewTaskKey = (task: ManualTask): string | null =>
  task.executorWorkflowExecutionId || null

export const AutomationExecutionsInbox: FC = () => {
  const {
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    hasItems,
    sortedItems
  } = useAutomationExecutionsInboxData()

  const {
    selectedAutomationExecution,
    isLegacyExecution,
    legacyWorkflowId,
    isPending: selectedAutomationExecutionIsPending
  } = useSelectedAutomationExecution()
  const navigate = useNavigate()

  const adminView = useShouldUseAdminManualTasks()

  const allExecutionIds = useMemo(
    () => sortedItems.map(item => item.executionId),
    [sortedItems]
  )

  // The id of the selected list item. Manual tasks are execution-backed here, so
  // they resolve to their execution's id alongside plain executions.
  const selectedId = isLegacyExecution
    ? `legacy-${legacyWorkflowId}`
    : selectedAutomationExecution?.id

  useEffect(() => {
    if (
      isPending ||
      selectedAutomationExecutionIsPending ||
      allExecutionIds.length === 0
    ) {
      return
    }

    if (!selectedId || !allExecutionIds.includes(selectedId)) {
      navigate(automationsPaths.executionHistoryPreview(allExecutionIds[0]), {
        replace: true
      })
      return
    }
  }, [
    isPending,
    selectedAutomationExecutionIsPending,
    allExecutionIds,
    navigate,
    selectedId
  ])

  return (
    <InboxList
      isPending={isPending}
      data-testid="automations.management.executionsHistory.list"
      headerActions={
        <Flex gap="8px" alignItems="center">
          <ExecutionAddFilter />
          <AdminSettingsButton
            onToggleOff={() => navigate(automationsPaths.executionHistory())}
          />
        </Flex>
      }
      filterRow={<ExecutionFilterPills />}
    >
      <>
        {!hasItems ? (
          <InboxList.EmptyState />
        ) : (
          <ListWithPagination
            isLoading={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          >
            {sortedItems.map(item =>
              match(item)
                .with({ kind: 'manualTask' }, ({ data: task, executionId }) => (
                  <ErrorBoundary
                    name="AutomationExecutionsInbox.manualTaskItem"
                    key={`manual-${task.id}`}
                  >
                    <Box
                      data-testid={`automations.management.executions.manualTasks.${task.id}`}
                    >
                      <ManualTaskItem
                        manualTask={task}
                        isSelected={selectedId === executionId}
                        hideUnreadIndicator
                        onClick={e => {
                          navigate(
                            automationsPaths.executionHistoryPreview(
                              executionId
                            )
                          )
                          e.stopPropagation()
                        }}
                      />
                    </Box>
                  </ErrorBoundary>
                ))
                .with({ kind: 'execution' }, ({ data: execution }) => (
                  <ErrorBoundary
                    name="AutomationExecutionsInbox.executionItem"
                    key={execution.id}
                  >
                    <AutomationExecutionItem
                      automationExecution={execution}
                      isSelected={
                        selectedAutomationExecution?.id === execution.id
                      }
                      data-testid={`automations.management.executions.automationExecutions.${execution.id}`}
                      onPreload={() =>
                        prefetchExecutionSummary({
                          executionId: execution.id,
                          adminView
                        })
                      }
                      onClick={e => {
                        navigate(
                          automationsPaths.executionHistoryPreview(execution.id)
                        )
                        e.stopPropagation()
                      }}
                    />
                  </ErrorBoundary>
                ))
                .with({ kind: 'legacyWorkflow' }, ({ data: workflow }) => (
                  <ErrorBoundary
                    name="AutomationExecutionsInbox.legacyWorkflowItem"
                    key={`legacy-${workflow.id}`}
                  >
                    <LegacyWorkflowItem
                      workflow={workflow}
                      isSelected={
                        isLegacyExecution && legacyWorkflowId === workflow.id
                      }
                      data-testid={`automations.management.executions.legacyWorkflows.${workflow.id}`}
                      onPreload={() =>
                        prefetchLegacyWorkflowSummary(workflow.id)
                      }
                      onClick={e => {
                        navigate(
                          automationsPaths.executionHistoryPreview(
                            `legacy-${workflow.id}`
                          )
                        )
                        e.stopPropagation()
                      }}
                    />
                  </ErrorBoundary>
                ))
                .exhaustive()
            )}
          </ListWithPagination>
        )}
      </>
    </InboxList>
  )
}

/**
 * Loads the combined list data for the "All" executions view.
 *
 * Manual tasks and automation executions are loaded in parallel and merged
 * into a single list sorted by created date (newest first). The status
 * filter is mapped to equivalent manual task statuses so that all manual
 * tasks matching the selected statuses are included.
 *
 * 1. Manual tasks and automation executions load in parallel.
 * 2. When scrolling, each page fetches from both sources simultaneously.
 * 3. Once both sources are fully loaded, legacy workflows begin loading.
 * 4. All three sources are merged: manual tasks and executions sorted by date,
 *    with legacy workflows appended at the bottom.
 */
const useAutomationExecutionsInboxData = () => {
  const pillFilters = useAtomValue(watchtowerFiltersAtom)
  const adminViewEnabled = useAtomValue(adminViewEnabledAtom)
  const { id: currentUserId } = useCurrentUser()

  const manualTaskStatusFilter = useMemo(
    () => mapExecutionStatusToManualTaskStatus(pillFilters.status),
    [pillFilters.status]
  )

  const shouldShowManualTasks = manualTaskStatusFilter !== null

  const filters: ExecutionFilters = useMemo(
    () => ({
      status:
        pillFilters.status.length > 0
          ? pillFilters.status.join(',')
          : undefined,
      workflowType:
        pillFilters.trigger.length > 0
          ? pillFilters.trigger.join(',')
          : undefined,
      requestorId: adminViewEnabled ? undefined : currentUserId
    }),
    [pillFilters, adminViewEnabled, currentUserId]
  )

  // --- Manual tasks (admin vs assigned, handled internally by useManualTasks) ---
  const {
    isPending: isManualTasksPending,
    isFetchingNextPage: isManualTasksFetchingNextPage,
    manualTasks,
    hasNextPage: manualTasksHasNextPage,
    fetchNextPage: manualTasksFetchNextPage
  } = useManualTasks({
    enabled: shouldShowManualTasks,
    status: manualTaskStatusFilter ?? undefined
  })

  // --- Automation executions (loaded in parallel with manual tasks) ---
  const {
    isPending: isExecutionsPending,
    isFetchingNextPage: isExecutionsFetchingNextPage,
    automationExecutions,
    fetchNextPage: executionsFetchNextPage,
    hasNextPage: executionsHasNextPage
  } = useAllAutomationExecutions({
    filters,
    enabled: true
  })

  // --- Legacy workflows (deferred until both modern sources are loaded) ---
  const allModernItemsLoaded =
    !manualTasksHasNextPage &&
    !isManualTasksPending &&
    !executionsHasNextPage &&
    !isExecutionsPending

  const {
    isPending: isLegacyPending,
    isFetchingNextPage: isLegacyFetchingNextPage,
    workflows: legacyWorkflows,
    hasNextPage: legacyHasNextPage,
    fetchNextPage: legacyFetchNextPage
  } = useLegacyWorkflows({
    filters: pillFilters,
    enabled: allModernItemsLoaded
  })

  // --- Deduplicate manual tasks: keep only the latest per execution ---
  const deduplicatedManualTasks = useMemo(() => {
    const latestByExecution = manualTasks.reduce<Map<string, ManualTask>>(
      (acc, task) => {
        const key = getExecutionsViewTaskKey(task)
        if (!key) return acc

        const existing = acc.get(key)
        const isNewer =
          !existing ||
          new Date(task.createdAt).getTime() >
            new Date(existing.createdAt).getTime()
        if (isNewer) acc.set(key, task)
        return acc
      },
      new Map()
    )

    return Array.from(latestByExecution.values())
  }, [manualTasks])

  // --- Deduplicate executions by ID and exclude those already shown as manual tasks ---
  const deduplicatedExecutions = useMemo(() => {
    const manualTaskExecutionIds = new Set(
      deduplicatedManualTasks
        .map(task => task.executorWorkflowExecutionId)
        .filter(Boolean)
    )
    return automationExecutions.reduce<typeof automationExecutions>(
      (acc, execution) => {
        if (manualTaskExecutionIds.has(execution.id)) return acc
        if (acc.some(e => e.id === execution.id)) return acc
        return [...acc, execution]
      },
      []
    )
  }, [deduplicatedManualTasks, automationExecutions])

  // --- Merge: manual tasks + executions sorted by date, legacy appended at bottom ---
  const sortedItems = useMemo(() => {
    const manualTaskItems: ListItem[] = shouldShowManualTasks
      ? deduplicatedManualTasks.map(task => ({
          kind: 'manualTask',
          // Dedup already filtered to execution-backed tasks, so the execution
          // id is guaranteed present.
          executionId: task.executorWorkflowExecutionId as string,
          createdAt: task.createdAt,
          data: task
        }))
      : []

    const executionItems: ListItem[] = deduplicatedExecutions.map(
      execution => ({
        kind: 'execution',
        executionId: execution.id,
        createdAt: execution.createTime,
        data: execution
      })
    )

    const modernItems = [...manualTaskItems, ...executionItems].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    const legacyItems: ListItem[] = legacyWorkflows.map(workflow => ({
      kind: 'legacyWorkflow',
      executionId: `legacy-${workflow.id}`,
      createdAt: workflow.createdAt,
      data: workflow
    }))

    return [...modernItems, ...legacyItems]
  }, [
    deduplicatedManualTasks,
    deduplicatedExecutions,
    legacyWorkflows,
    shouldShowManualTasks
  ])

  // --- Combined pagination interface ---
  const isPending = isManualTasksPending || isExecutionsPending
  const hasNextPage =
    manualTasksHasNextPage || executionsHasNextPage || legacyHasNextPage
  const isFetchingNextPage =
    isManualTasksFetchingNextPage ||
    isExecutionsFetchingNextPage ||
    isLegacyPending ||
    isLegacyFetchingNextPage

  // Fetch next page from both modern sources in parallel, then legacy
  const fetchNextPage = useCallback(() => {
    const hasModernPages = manualTasksHasNextPage || executionsHasNextPage
    if (hasModernPages) {
      if (manualTasksHasNextPage) manualTasksFetchNextPage()
      if (executionsHasNextPage) executionsFetchNextPage()
    } else if (legacyHasNextPage) {
      legacyFetchNextPage()
    }
  }, [
    manualTasksHasNextPage,
    executionsHasNextPage,
    legacyHasNextPage,
    manualTasksFetchNextPage,
    executionsFetchNextPage,
    legacyFetchNextPage
  ])

  return {
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    hasItems: sortedItems.length > 0,
    sortedItems
  }
}
